'use client';

import { useEffect } from 'react';
import { onChatExpandedChange } from './Actions';
import { subscribeLauncherClearance, setLauncherClearance } from './launcherClearance';

const OSANO_DIALOG_SELECTOR = '.osano-cm-dialog';
const OSANO_HIDDEN_CLASS = 'osano-cm-dialog--hidden';
const OSANO_KEY = 'osano-consent-dialog';

// Osano keeps the dismissed bar's box in the layout and cross-fades it over
// 0.7s, so neither its height nor its computed styles describe the current
// state: at the moment the class changes, showing still reads `opacity: 0` and
// hiding still reads `visibility: visible`. The class is the one signal that
// flips exactly when the bar starts coming or going.
const isDialogShowing = (dialog) =>
  !dialog.classList.contains(OSANO_HIDDEN_CLASS) && getComputedStyle(dialog).display !== 'none';

const watchOsanoDialog = (dialog) => {
  const report = () => {
    setLauncherClearance(OSANO_KEY, isDialogShowing(dialog) ? dialog.offsetHeight : 0);
  };

  const resizeObserver = new ResizeObserver(report);
  resizeObserver.observe(dialog);
  const attributeObserver = new MutationObserver(report);
  attributeObserver.observe(dialog, { attributes: true, attributeFilter: ['class', 'style'] });
  report();

  return () => {
    resizeObserver.disconnect();
    attributeObserver.disconnect();
    setLauncherClearance(OSANO_KEY, 0);
  };
};

/**
 * BL-924: keeps Kodif's chat launcher clear of anything pinned to the bottom of
 * the viewport — the PDP sticky price bar and Osano's consent bar today. The
 * launcher is a fixed iframe at `bottom: 15px` with z-index 2147483647, so it
 * can only be moved, never stacked under. Publishes the tallest registered
 * height as `--kodif-launcher-offset`; `html.kodif-launcher-offset` in
 * globals.css applies it, and eases the launcher between offsets so it tracks
 * the sticky bar rather than jumping.
 */
export default function KodifLauncherOffset() {
  useEffect(() => {
    const root = document.documentElement;
    let clearance = 0;
    let isChatExpanded = false;

    const apply = () => {
      // The expanded chat panel is as tall as the viewport, so lifting it
      // would push its header off screen — leave it where Kodif puts it.
      if (clearance > 0 && !isChatExpanded) {
        root.style.setProperty('--kodif-launcher-offset', `${clearance}px`);
        root.classList.add('kodif-launcher-offset');
        return;
      }
      root.classList.remove('kodif-launcher-offset');
      root.style.removeProperty('--kodif-launcher-offset');
    };

    const unsubscribeClearance = subscribeLauncherClearance((height) => {
      clearance = height;
      apply();
    });

    const unsubscribeChat = onChatExpandedChange((expanded) => {
      isChatExpanded = expanded;
      apply();
    });

    // Osano loads itself, so the dialog is usually not in the DOM yet on mount.
    let unwatchOsano = null;
    const existing = document.querySelector(OSANO_DIALOG_SELECTOR);
    let domObserver = null;

    if (existing) {
      unwatchOsano = watchOsanoDialog(existing);
    } else {
      domObserver = new MutationObserver(() => {
        const dialog = document.querySelector(OSANO_DIALOG_SELECTOR);
        if (!dialog) return;
        domObserver.disconnect();
        domObserver = null;
        unwatchOsano = watchOsanoDialog(dialog);
      });
      domObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      unsubscribeClearance();
      unsubscribeChat();
      domObserver?.disconnect();
      unwatchOsano?.();
      root.classList.remove('kodif-launcher-offset');
      root.style.removeProperty('--kodif-launcher-offset');
    };
  }, []);

  return null;
}
